import { useTranslation } from "react-i18next";
import { Badge, Button, Input } from "../../ui";
import type { QuoteWizard } from "../useQuoteWizard";
import { TollsEditor } from "../TollsEditor";
import { QuoteLinesTable } from "../QuoteLinesTable";
import { QuoteTotals, formatDuration } from "../QuoteTotals";

interface CalculationStepProps {
  wizard: QuoteWizard;
}

function SectionTitle({ children, trailing }: { children: string; trailing?: React.ReactNode }) {
  return (
    <div className="mt-4 flex items-center justify-between border-b border-line pb-2 first:mt-0">
      <h4 className="text-sm font-semibold text-content">{children}</h4>
      {trailing}
    </div>
  );
}

/** Paso 2 — distancia/tiempo, casetas, desglose editable, margen y precio. */
export function CalculationStep({ wizard }: CalculationStepProps) {
  const { t } = useTranslation("finanzasOperativas");
  const { server, estimate, overrides, setOverrides, manualTolls, setManualTolls, estimating, estimateError, runEstimate } = wizard;

  if (!estimate || !server) {
    return (
      <div className="flex min-h-64 flex-col items-center justify-center gap-3">
        {estimating ? (
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
        ) : (
          <>
            <p className="text-sm text-error">{t("quotes.calculation.calcError")}</p>
            {estimateError && <p className="text-xs text-content-faint">{String((estimateError as Error).message)}</p>}
            <Button size="sm" onClick={() => void runEstimate()}>
              {t("common.retry")}
            </Button>
          </>
        )}
      </div>
    );
  }

  const profile = server.inputs.profile;

  return (
    <div className="space-y-3">
      <SectionTitle
        trailing={
          <Button variant="outline" size="sm" loading={estimating} icon={<i className="ri-refresh-line" />} onClick={() => void runEstimate()}>
            {t("quotes.calculation.recalculate")}
          </Button>
        }
      >
        {t("quotes.calculation.distanceDuration")}
      </SectionTitle>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Input
          label={`${t("quotes.fields.distance")} (km)`}
          type="number"
          min={0}
          step="0.1"
          value={wizard.distanceOverride ?? estimate.distance_km}
          onChange={(e) => wizard.setDistanceOverride(e.target.value === "" ? null : Number(e.target.value))}
        />
        <Input
          label={`${t("quotes.fields.duration")} (min)`}
          type="number"
          min={0}
          hint={formatDuration(wizard.durationOverride ?? estimate.duration_min)}
          value={wizard.durationOverride ?? estimate.duration_min}
          onChange={(e) => wizard.setDurationOverride(e.target.value === "" ? null : Number(e.target.value))}
        />
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-content">{t("quotes.fields.days")}</span>
          <span className="flex min-h-10 items-center px-3 text-sm text-content">{estimate.days}</span>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-content">{t("quotes.calculation.profileUsed")}</span>
          <span className="flex min-h-10 items-center gap-2 px-1 text-xs text-content-secondary">
            <span className="truncate">{profile.name ?? t("quotes.service.systemProfile")}</span>
            {profile.source === "system_default" && (
              <Badge tone="warning" variant="outline" size="sm">
                {t("costProfiles.systemDefault")}
              </Badge>
            )}
          </span>
        </div>
      </div>
      {(wizard.distanceOverride != null || wizard.durationOverride != null) && (
        <p className="text-xs text-content-faint">{t("quotes.calculation.recalculate")} →</p>
      )}

      <SectionTitle>{t("quotes.calculation.tollsTitle")}</SectionTitle>
      <TollsEditor
        tolls={estimate.tolls}
        manualItems={manualTolls}
        onChange={setManualTolls}
        onRefresh={() => {
          setManualTolls(null);
          void runEstimate({ refresh_tolls: true, tolls_override: null });
        }}
        refreshing={estimating}
        error={server.inputs.tolls_error?.message ?? null}
        currency={estimate.currency}
      />

      <SectionTitle>{t("quotes.calculation.linesTitle")}</SectionTitle>
      <QuoteLinesTable lines={estimate.lines} baseLines={server.estimate.lines} overrides={overrides} onChange={setOverrides} currency={estimate.currency} />

      <SectionTitle>{t("quotes.calculation.marginTitle")}</SectionTitle>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="space-y-3 sm:col-span-1">
          <Input
            label={`${t("quotes.fields.margin")} (%)`}
            type="number"
            min={0}
            max={95}
            step="0.5"
            value={estimate.margin_pct}
            onChange={(e) => setOverrides({ ...overrides, margin_pct: Number(e.target.value), price: undefined })}
          />
          <Input
            label={`${t("quotes.fields.price")} (${estimate.currency})`}
            type="number"
            min={0}
            step="0.01"
            value={estimate.price}
            onChange={(e) => setOverrides({ ...overrides, price: Number(e.target.value), margin_pct: undefined })}
          />
          <p className="text-xs text-content-faint">{t("quotes.calculation.marginHint")}</p>
        </div>
        <div className="sm:col-span-2">
          <QuoteTotals estimate={estimate} />
        </div>
      </div>
    </div>
  );
}
